import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Phone,
  Clock,
  CalendarDays,
  CheckSquare,
  Users,
  BarChart3,
  FileCheck,
  ClipboardList,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronRight,
  DollarSign,
  Percent,
} from "lucide-react";

const staffLinks = [
  { path: "/portal", label: "Dashboard", icon: LayoutDashboard },
  { path: "/portal/activities", label: "Log Activity", icon: Phone },
  { path: "/portal/signups", label: "Signups", icon: DollarSign },
  { path: "/portal/timesheet", label: "Timesheet", icon: Clock },
  { path: "/portal/leave", label: "Leave", icon: CalendarDays },
  { path: "/portal/todos", label: "Tasks", icon: CheckSquare },
  { path: "/portal/commissions", label: "My Commissions", icon: Percent },
];

const adminLinks = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/staff", label: "Staff", icon: Users },
  { path: "/admin/signups", label: "Signups", icon: DollarSign },
  { path: "/admin/commissions", label: "Commissions", icon: Percent },
  { path: "/admin/performance", label: "Performance", icon: BarChart3 },
  { path: "/admin/todos", label: "Staff Tasks", icon: CheckSquare },
  { path: "/admin/leaves", label: "Leave Requests", icon: CalendarDays },
  { path: "/admin/timesheets", label: "Timesheets", icon: FileCheck },
  { path: "/admin/audit", label: "Audit Logs", icon: ClipboardList },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isAdminSection = location.pathname.startsWith("/admin");
  const links = isAdminSection ? adminLinks : staffLinks;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    return name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "U";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="font-bold text-lg text-slate-900">
            {isAdminSection ? "Immigo Admin" : "Immigo Staff"}
          </h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="mobile-user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-900">Immigo Staff</h1>
                  <p className="text-xs text-slate-500">{isAdminSection ? "Admin" : "Staff"} Panel</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {links.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`sidebar-link ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{link.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
            
            {/* Admin can switch to staff view */}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-200">
                <Link
                  to={isAdminSection ? "/portal" : "/admin"}
                  className="sidebar-link text-blue-600"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="switch-portal-btn"
                >
                  {isAdminSection ? (
                    <>
                      <LayoutDashboard className="h-5 w-5" />
                      <span>Staff View</span>
                    </>
                  ) : (
                    <>
                      <Shield className="h-5 w-5" />
                      <span>Admin Panel</span>
                    </>
                  )}
                </Link>
              </div>
            )}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="user-profile-dropdown"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
